// ink-component
import React, { ReactNode } from 'react';
import type { StrictModifiers } from '@popperjs/core';
import { ThemeProvider } from 'styled-components';
import Icon from '../Icon/Icon';
import Tooltip from '../Tooltip/Tooltip';
import getGlobalProps from '../../helpers/constants/global-props';
import { HelpContent, HelpIcon, HelpLink, HelpText, HelpTrigger, HelpWrapper, FieldHelpText } from './styles';

type Type = 'tooltip' | 'field';
type TooltipDirection = 'left' | 'right' | 'top' | 'bottom';

export interface HelpProps {
  /** A text with general explanation about the paragraph, field or column */
  text?: ReactNode;
  /** An optional node below the text, usually an Anchor component directing
   * the user to more information. For example:
   * `<Anchor href="...">...</Anchor>` */
  link?: ReactNode;
  /** The type of the help.
   *  Can be used as a field for Forms or a tooltip for Tables for example. */
  type?: Type;
  /** Useful for tracking engagement. Applicable if `type` is `tooltip` */
  onHover?: () => void;
  /** The direction the tooltip will be opened, if the `type` is `tooltip` */
  tooltipDirection: TooltipDirection;
  /** Customize the Tooltip behavior passing an array to configure
   * any of the **prebuilt** Popper v2 modifiers:
   * https://popper.js.org/docs/v2/constructors/#modifiers */
  tooltipModifiers: StrictModifiers[] | [];
  /** Removes time delay between trigger losing hover/focus and the dismissal of the tooltip content */
  noDelay?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const globalProps = getGlobalProps({});

/**
 * @purpose Explains concepts contextually
 * @categories accessory
 * @aka tooltip, education
 */
function Help({ text, link, type, tooltipDirection, tooltipModifiers, noDelay, onHover, ...otherProps }: HelpProps) {
  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        type,
      })}
    >
      <HelpWrapper {...otherProps} data-testid={otherProps['data-testid']} {...globalProps}>
        {type === 'tooltip' && (
          <Tooltip
            placement={tooltipDirection}
            modifiers={tooltipModifiers}
            noDelay={noDelay}
            onHover={onHover}
            trigger={
              <HelpTrigger type="button">
                <Icon type="questionmark" size="lg" aria-label="Help" />
              </HelpTrigger>
            }
          >
            {text && <HelpText>{text}</HelpText>}
            {link && <HelpText>{link}</HelpText>}
          </Tooltip>
        )}
        {type !== 'tooltip' && (
          <HelpContent>
            {text && <FieldHelpText>{text}</FieldHelpText>}
            {link && (
              <HelpLink>
                <HelpIcon>
                  <Icon type="questionmark" size="lg" aria-label="Help" />
                </HelpIcon>
                {link}
              </HelpLink>
            )}
          </HelpContent>
        )}
      </HelpWrapper>
    </ThemeProvider>
  );
}

Help.defaultProps = {
  text: undefined,
  link: undefined,
  type: undefined,
  tooltipDirection: 'top',
  tooltipModifiers: [],
  'data-testid': undefined,
};

Help.displayName = 'Help';

export default Help;
